import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Stack,
  Typography,
  Button
} from '@material-ui/core';
import SearchNotFound from '../SearchNotFound';
import { UserListHead, UserListToolbar } from '../user';
import RemoveCompanyAdminButton from './RemoveCompanyAdminButton';
import AddCompanyAdminDialog from './AddCompanyAdminDialog';
import { applySortFilter, getComparator } from '../../utils/sort';

const adminTableHead = [{ id: 'email', label: 'Admin Email', alignRight: false }, { id: '' }];

CompanyAdminTable.propTypes = {
  admins: PropTypes.array.isRequired,
  updateAdmins: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
  showHeader: PropTypes.bool
};

CompanyAdminTable.defaultProps = {
  showHeader: false
};

export default function CompanyAdminTable({ admins, updateAdmins, companyId, showHeader }) {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('email');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleAdminOpenDialog = () => {
    setAdminDialogOpen(true);
  };

  const handleAdminCloseDialog = () => {
    setAdminDialogOpen(false);
  };

  const filteredAdmins = applySortFilter(admins, getComparator(order), filterName, 'email');
  const isAdminNotFound = filteredAdmins.length === 0;

  return (
    <>
      {showHeader && (
        <>
          <AddCompanyAdminDialog
            isOpen={adminDialogOpen}
            companyId={companyId}
            updateAdmins={updateAdmins}
            closeHandler={handleAdminCloseDialog}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
            <Typography variant="subtitle1">Company Admins</Typography>
            <Button onClick={handleAdminOpenDialog} variant="contained">
              Add Admin
            </Button>
          </Stack>
        </>
      )}
      <UserListToolbar
        disableGutters
        variant="dense"
        sx={{ margin: 0 }}
        placeholder="Search admins..."
        filterName={filterName}
        onFilterName={handleFilterByName}
      />
      <TableContainer data-testid="company-admin-table" sx={{ maxHeight: 350 }}>
        <Table size="small">
          <UserListHead
            order={order}
            orderBy={orderBy}
            headLabel={adminTableHead}
            rowCount={admins.length}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {filteredAdmins != null &&
              filteredAdmins
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover key={row.email} tabIndex={-1}>
                    <TableCell align="left">{row.email}</TableCell>
                    <TableCell align="right">
                      <RemoveCompanyAdminButton
                        email={row.email}
                        companyId={companyId}
                        updateAdmins={updateAdmins}
                        variant="icononly"
                      />
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
          {isAdminNotFound && (
            <TableBody>
              <TableRow>
                <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                  <SearchNotFound searchQuery={filterName} />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredAdmins.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
